import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Box } from '@mui/joy';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Typography from '@mui/joy/Typography';

import { formatTimestamp } from '../../general-utilities/time-util';
import { CampaignStatus } from '../../models/CampaignStatus.enum';
import { CampaignService } from '../../services/CampaignService';
import { User, Campaign } from '../../services/types/api.generated';
import campaignstore from '../../stores/CampaignStore';
import { CampaignBadge } from './CampaignBadge';

interface CampaignCardProps {
  campaign: Campaign;
  user: User | null;
  joinCampaign: (campaignId: string) => void;
  activeCampaign?: Campaign;
}

export default function CampaignCard({ campaign, user, joinCampaign, activeCampaign }: CampaignCardProps) {
  const isActive = campaign.status === 1;
  const { t, i18n } = useTranslation();

  const handleJoinCampaign = async (campaignId: string) => {
    try {
      const isAdded = await CampaignService.addUsersInCampaign(campaignId);

      if (isAdded) {
        toast.success(t('users_dropdown.campaign_joined'));
        joinCampaign(campaignId);
      } else {
        toast.error(t('users_dropdown.join_failed'));
      }
    } catch (error) {
      console.error(t('users_dropdown.join_failed'));
      toast.error(t('users_dropdown.join_failed'));
    }
  };

  const handleAlreadyJoined = (campaignId: string) => {
    joinCampaign(campaignId);
  };

  const handleNavigateToCampaign = (campaignId: string) => {
    if (!user) {
      toast.error(t('users_dropdown.login_required'));
      return;
    }
    if (campaign.users?.includes(user.id)) {
      handleAlreadyJoined(campaignId);
    } else {
      handleJoinCampaign(campaignId);
    }
  };

  const getFooter = () => {
    if (isActive) {
      return (
        <div className='flex flex-row gap-4'>
          <Button
            className='font-semibold'
            color='neutral'
            onClick={() => campaignstore.stopCampaign(campaign.id, t)}
            size='md'
            variant='outlined'
          >
            {t('campaigns.end')}
          </Button>
          <Button
            className='font-semibold self-center !ml-auto'
            color='primary'
            onClick={() => handleNavigateToCampaign(campaign?.id)}
            size='md'
            variant='solid'
          >
            {t('campaigns.join')}
          </Button>
        </div>
      );
    }
    return (
      <div className='flex'>
        <div className='flex items-center mr-4'>
          <Typography className='text-sm' level='body-md'>
            {campaign.enable_vantis_send ? t('campaigns.vantis_status_active') : t('campaigns.vantis_status_inactive')}
          </Typography>
        </div>
        <Button
          aria-label='Start Campaign'
          className='font-semibold h-0 !ml-auto'
          color='primary'
          onClick={() => campaignstore.startCampaign(campaign.id, t)}
          size='md'
          variant='solid'
          disabled={activeCampaign && activeCampaign?.id !== campaign?.id}
        >
          {t('campaigns.start')}
        </Button>
      </div>
    );
  };
  const getCampaignStatus = () => {
    if (campaign.status === 1) {
      return CampaignStatus.ongoing;
    }
    if (campaign.status === 0) {
      // If it was started before and is now inactive, treat as ended
      if (campaign.start_time) return CampaignStatus.ended;
      return CampaignStatus.neverStarted;
    }
    return CampaignStatus.unknown;
  };

  const getStartEndTimeSection = () => {
    if (!campaign.start_time) {
      return null; // No start time, return nothing
    }

    const endTimeText = isActive
      ? t('campaigns.badge.ongoing')
      : campaign.end_time
      ? formatTimestamp(campaign.end_time, i18n.language)
      : t('campaigns.badge.ended');

    return (
      <Box
        className='flex flex-col gap-4 p-4 border rounded-lg mt-4'
        sx={{
          backgroundColor: 'background.level1',
          borderColor: 'divider'
        }}
      >
        {/* Start Time */}
        <Box className='w-full'>
          <Typography className='text-lg font-medium' level='body-md'>
            {t('campaigns.start_label')}
          </Typography>
          <Typography className='whitespace-nowrap overflow-hidden text-ellipsis text-md' level='body-md'>
            {formatTimestamp(campaign.start_time, i18n.language)}
          </Typography>
        </Box>

        {/* End Time */}
        <Box className='w-full'>
          <Typography className='text-lg font-medium' level='body-md'>
            {t('campaigns.end_label')}
          </Typography>
          <Typography className='whitespace-nowrap overflow-hidden text-ellipsis text-md' level='body-md'>
            {endTimeText}
          </Typography>
        </Box>
      </Box>
    );
  };

  const getDroneSection = () => {
    return (
      <Box>
        {(campaign.device_details || []).map((drone) => (
          <Typography key={drone.id} level='body-md'>
            {drone.name}
          </Typography>
        ))}
      </Box>
    );
  };
  return (
    <Card
      className='w-80 flex flex-col justify-between h-full border  rounded-lg shadow-md'
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <Box
        className='flex-1 flex flex-col justify-start gap-4 p-2'
        sx={{
          padding: 0.5
        }}
      >
        <Box className='flex justify-between'>
          <Typography className='text-xl font-bold' level='title-lg'>
            {campaign.name}
          </Typography>
          <CampaignBadge status={getCampaignStatus()} />
        </Box>
        <Typography className=' mb-2' level='body-md' sx={{ color: 'text.secondary' }}>
          {campaign.description}
        </Typography>
        {getDroneSection()}
        {getStartEndTimeSection()}
      </Box>

      <CardContent
        sx={{
          paddingTop: 2,
          paddingBottom: 2
        }}
      >
        {getFooter()}
      </CardContent>
    </Card>
  );
}
